import { useState, useEffect, useCallback } from 'react';
import Head from 'next/head';
import Layout from '@/components/Layout';
import { useAuth } from '@/lib/authContext';
import { useRouter } from 'next/router';
import { supabase } from '@/lib/supabase';
import type { DashboardFinancieroAsesor, AlertaFinanciera } from '@/types';
import FinancialChart from '@/components/FinancialChart';
import MetricCard from '@/components/MetricCard';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

// ─── Icon helper ─────────────────────────────────────────
function Icon({ name, className }: { name: string; className?: string }) {
  return (
    <span className={cn('material-symbols-outlined', className)}>{name}</span>
  );
}

interface ClienteDetalleModelo {
  cliente_id: string;
  periodo: string;
  ejercicio_fiscal: string;
  ingresos_operacionales: string;
  gastos_operacionales: string;
  resultado_neto: string;
  margen_neto: string;
  ratio_liquidez?: string;
  ratio_solvencia?: string;
  roi?: string;
  roe?: string;
  comentario_ia?: string;
  alertas_financieras?: AlertaFinanciera[];
  recomendaciones_ia?: string[];
  clientes?: {
    razon_social: string;
    nombre_comercial?: string;
    nif?: string;
  };
}

/**
 * ADVISOR PORTFOLIO FINANCIAL DASHBOARD
 * 
 * Shows aggregated financial data for assigned clients only
 * with AI alerts, risk indicators, and quick access to client details
 */
export default function AsesorPanelFinancieroPortfolio() {
  const { user, loading } = useAuth();
  const router = useRouter();
  
  const [dashboardData, setDashboardData] = useState<DashboardFinancieroAsesor | null>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [periodoSeleccionado, setPeriodoSeleccionado] = useState<string>(new Date().getFullYear().toString());
  const [clienteDetalle, setClienteDetalle] = useState<ClienteDetalleModelo | null>(null);
  const [mostrarDetalle, setMostrarDetalle] = useState(false);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoadingData(true);

      // Get advisor's assigned clients
      const { data: assignedClients, error: clientsError } = await supabase
        .from('asesor_cliente')
        .select('cliente_id, clientes(*)')
        .eq('asesor_id', user?.id);

      if (clientsError) throw clientsError;

      const clientIds = assignedClients?.map(ac => ac.cliente_id) || [];

      if (clientIds.length === 0) {
        setDashboardData({
          clientesAsignados: 0,
          totalIngresos: 0,
          totalGastos: 0,
          resultadoNeto: 0,
          documentosPendientes: 0,
          incidenciasAbiertas: 0,
          clientesConAlertas: []
        });
        return;
      }

      // Get financial data for assigned clients from datos_financieros
      const { data: modelos, error: modelosError } = await supabase
        .from('datos_financieros')
        .select('*, clientes(razon_social, nombre_comercial, nif)')
        .in('cliente_id', clientIds)
        .like('periodo', `${periodoSeleccionado}-%`);

      if (modelosError) throw modelosError;

      // Get pending documents
      const { data: pendingDocs } = await supabase
        .from('documentos')
        .select('id')
        .in('cliente_id', clientIds)
        .in('estado', ['pendiente', 'procesado_ia']);

      // Get open incidents
      const { data: openIncidents } = await supabase
        .from('incidencias')
        .select('id')
        .in('cliente_id', clientIds)
        .in('estado', ['nueva', 'en_revision']);

      // Calculate aggregates using datos_financieros column names
      const totalIngresos = (modelos || []).reduce((sum, m) => sum + (parseFloat(m.ingresos) || 0), 0);
      const totalGastos = (modelos || []).reduce((sum, m) => sum + (parseFloat(m.gastos) || 0), 0);
      const resultadoNeto = totalIngresos - totalGastos;

      // Identify clients with high risk as alerts
      const clientesConAlertas = (modelos || [])
        .filter(m => m.riesgo_ia && m.riesgo_ia !== 'bajo')
        .map(m => ({
          clienteId: m.cliente_id,
          nombreCliente: m.clientes?.nombre_comercial || m.clientes?.razon_social || 'Cliente',
          alertas: [{ tipo: (m.riesgo_ia === 'alto' ? 'danger' : 'warning') as 'warning' | 'danger', titulo: `Riesgo ${m.riesgo_ia}`, descripcion: `Riesgo ${m.riesgo_ia} detectado en periodo ${m.periodo}` }],
          resultado: parseFloat(m.resultado) || 0
        }));

      setDashboardData({
        clientesAsignados: clientIds.length,
        totalIngresos,
        totalGastos,
        resultadoNeto,
        documentosPendientes: pendingDocs?.length || 0,
        incidenciasAbiertas: openIncidents?.length || 0,
        clientesConAlertas
      });

    } catch (error: unknown) {
      console.error('Error loading advisor dashboard:', error);
      toast.error('Error al cargar datos del portfolio');
    } finally {
      setLoadingData(false);
    }
  }, [user, periodoSeleccionado]);

  useEffect(() => {
    if (!loading && (!user || user.rol !== 'asesor')) {
      router.push('/');
    }
    if (user && user.rol === 'asesor') {
      loadDashboardData();
    }
  }, [user, loading, router, loadDashboardData]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const getAlertBadgeClasses = (tipo: string) => {
    const badges: Record<string, string> = {
      danger: 'bg-red-50 text-red-700',
      warning: 'bg-amber-50 text-amber-700',
      info: 'bg-slate-100 text-slate-700',
    };
    return badges[tipo] || '';
  };

  const handleVerCliente = async (clienteId: string) => {
    try {
      // Get detailed client financial data
      const { data: modelo, error: modeloError } = await supabase
        .from('modelos_financieros_cliente')
        .select('*, clientes(razon_social, nombre_comercial, nif)')
        .eq('cliente_id', clienteId)
        .eq('ejercicio_fiscal', periodoSeleccionado)
        .single();

      if (modeloError) throw modeloError;

      setClienteDetalle(modelo as unknown as ClienteDetalleModelo);
      setMostrarDetalle(true);
    } catch (error) {
      console.error('Error loading client detail:', error);
      toast.error('Error al cargar detalle del cliente');
    }
  };

  if (loading || loadingData) {
    return (
      <Layout rol="asesor">
        <div className="px-12 pb-12 space-y-12 pt-8">
          <Skeleton className="h-12 w-72 rounded-huge" />
          <Skeleton className="h-4 w-96 rounded-huge" />
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            <Skeleton className="h-24 rounded-huge" />
            <Skeleton className="h-24 rounded-huge" />
            <Skeleton className="h-24 rounded-huge" />
            <Skeleton className="h-24 rounded-huge" />
            <Skeleton className="h-24 rounded-huge" />
          </div>
          <Skeleton className="h-32 rounded-huge" />
          <Skeleton className="h-64 rounded-huge" />
        </div>
      </Layout>
    );
  }


  return (
    <>
      <Head>
        <title>Portfolio Financiero - Asesor - Externaliza2</title>
      </Head>
      <Layout rol="asesor">
        <div className="h-[calc(100vh-72px)] overflow-y-auto px-12 pb-12 space-y-12 pt-8">
          {/* Header */}
          <div className="flex justify-between items-end">
            <div>
              <h1 className="page-title">Portfolio Financiero</h1>
              <p className="page-subtitle">
                Vista consolidada de tus {dashboardData?.clientesAsignados || 0} clientes asignados
              </p>
            </div>
            <div className="flex gap-3">
              <Select
                value={periodoSeleccionado}
                onValueChange={setPeriodoSeleccionado}
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2024">2024</SelectItem>
                  <SelectItem value="2023">2023</SelectItem>
                  <SelectItem value="2022">2022</SelectItem>
                </SelectContent>
              </Select>
              <button onClick={loadDashboardData} className="ghost-btn">
                <Icon name="refresh" className="text-[18px]" />
                Actualizar
              </button>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            <MetricCard
              titulo="Clientes Asignados"
              valor={dashboardData?.clientesAsignados || 0}
              descripcion="En tu portfolio"
              icono="group"
              href="/asesor/clientes"
            />
            <MetricCard
              titulo="Ingresos Totales"
              valor={formatCurrency(dashboardData?.totalIngresos || 0)}
              descripcion="Todos tus clientes"
              icono="trending_up"
            />
            <MetricCard
              titulo="Resultado Neto"
              valor={formatCurrency(dashboardData?.resultadoNeto || 0)}
              descripcion={
                dashboardData && dashboardData.resultadoNeto >= 0
                  ? 'Positivo'
                  : 'Requiere atención'
              }
              icono={dashboardData && dashboardData.resultadoNeto >= 0 ? 'check_circle' : 'warning'}
            />
            <MetricCard
              titulo="Docs Pendientes"
              valor={dashboardData?.documentosPendientes || 0}
              descripcion="Requieren revisión"
              icono="description"
              href="/asesor/documentos-pendientes"
            />
            <MetricCard
              titulo="Incidencias"
              valor={dashboardData?.incidenciasAbiertas || 0}
              descripcion="Sin resolver"
              icono="report_problem"
              href="/asesor/incidencias"
            />
          </div>

          {/* Portfolio Health Summary */}
          <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8">
            <div className="flex items-start gap-4">
              <Icon name="monitoring" className="text-[28px] text-calm-accent" />
              <div className="flex-1">
                <h3 className="section-title mb-6">Resumen del Portfolio</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div>
                    <div className="stat-label">Ingresos Promedio</div>
                    <div className="stat-value mt-1">
                      {formatCurrency(
                        dashboardData && dashboardData.clientesAsignados > 0
                          ? dashboardData.totalIngresos / dashboardData.clientesAsignados
                          : 0
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="stat-label">Margen Promedio</div>
                    <div className="stat-value mt-1">
                      {dashboardData && dashboardData.totalIngresos > 0
                        ? ((dashboardData.resultadoNeto / dashboardData.totalIngresos) * 100).toFixed(1)
                        : 0}%
                    </div>
                  </div>
                  <div>
                    <div className="stat-label">Clientes con Alertas</div>
                    <div className="stat-value mt-1 text-amber-600">
                      {dashboardData?.clientesConAlertas.length || 0}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Clients with Alerts */}
          {dashboardData && dashboardData.clientesConAlertas.length > 0 && (
            <div className="bg-gradient-to-br from-white to-amber-50/20 border border-amber-200/40 rounded-huge p-8">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <Icon name="notifications_active" className="text-[22px] text-amber-600" />
                  <h2 className="section-title">Clientes con Alertas Financieras</h2>
                </div>
                <Badge variant="outline" className="badge-warning text-[9px] uppercase tracking-wider">
                  {dashboardData.clientesConAlertas.length}
                </Badge>
              </div>

              <div className="space-y-3">
                {dashboardData.clientesConAlertas.map((cliente, idx) => (
                  <div
                    key={idx}
                    className="border-l-2 border-amber-400 bg-slate-50/50 rounded-2xl p-5 hover:bg-slate-50 transition-all duration-300 cursor-pointer"
                    onClick={() => handleVerCliente(cliente.clienteId)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <h3 className="text-sm font-medium text-calm-text">{cliente.nombreCliente}</h3>
                          <Badge
                            variant={cliente.resultado >= 0 ? 'outline' : 'destructive'}
                            className={cn(
                              'text-[9px] uppercase tracking-wider',
                              cliente.resultado >= 0 && 'badge-success'
                            )}
                          >
                            {formatCurrency(cliente.resultado)}
                          </Badge>
                        </div>

                        <div className="space-y-2">
                          {cliente.alertas.map((alerta: AlertaFinanciera, aIdx: number) => (
                            <div key={aIdx} className="flex items-start gap-2">
                              <Badge
                                variant="outline"
                                className={cn('text-[9px] uppercase tracking-wider', getAlertBadgeClasses(alerta.tipo as string))}
                              >
                                {(alerta.tipo as string).toUpperCase()}
                              </Badge>
                              <div className="flex-1">
                                <div className="text-sm font-light text-calm-text">{alerta.titulo as string}</div>
                                <div className="text-[11px] text-calm-muted font-light">{alerta.descripcion as string}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVerCliente(cliente.clienteId);
                        }}
                        className="ghost-btn text-xs ml-4"
                      >
                        <Icon name="open_in_new" className="text-[16px]" />
                        Ver Detalle
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div
              className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge p-8 hover:shadow-card-hover transition-all duration-300 cursor-pointer group"
              onClick={() => router.push('/asesor/documentos-pendientes')}
            >
              <div className="flex items-center gap-4">
                <Icon name="description" className="text-[28px] text-calm-accent group-hover:text-calm-text transition-colors duration-300" />
                <div>
                  <div className="text-sm font-medium text-calm-text">Revisar Documentos</div>
                  <div className="text-[10px] uppercase tracking-widest text-calm-accent mt-1">
                    {dashboardData?.documentosPendientes || 0} pendientes
                  </div>
                </div>
              </div>
            </div>

            <div
              className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8 hover:shadow-card-hover transition-all duration-300 cursor-pointer group"
              onClick={() => router.push('/asesor/contabilizacion-ia')}
            >
              <div className="flex items-center gap-4">
                <Icon name="auto_stories" className="text-[28px] text-calm-accent group-hover:text-calm-text transition-colors duration-300" />
                <div>
                  <div className="text-sm font-medium text-calm-text">Generar Libros</div>
                  <div className="text-[10px] uppercase tracking-widest text-calm-accent mt-1">Libro Registro</div>
                </div>
              </div>
            </div>

            <div
              className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge p-8 hover:shadow-card-hover transition-all duration-300 cursor-pointer group"
              onClick={() => router.push('/asesor/clientes-detailed')}
            >
              <div className="flex items-center gap-4">
                <Icon name="group" className="text-[28px] text-calm-accent group-hover:text-calm-text transition-colors duration-300" />
                <div>
                  <div className="text-sm font-medium text-calm-text">Gestionar Clientes</div>
                  <div className="text-[10px] uppercase tracking-widest text-calm-accent mt-1">
                    {dashboardData?.clientesAsignados || 0} asignados
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Client Detail Modal */}
          <Dialog open={mostrarDetalle} onOpenChange={(open) => {
            if (!open) {
              setMostrarDetalle(false);
              setClienteDetalle(null);
            }
          }}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="section-title">
                  {clienteDetalle?.clientes?.nombre_comercial || clienteDetalle?.clientes?.razon_social}
                </DialogTitle>
                <p className="text-sm text-calm-muted font-light">
                  {clienteDetalle?.clientes?.nif} · Periodo: {clienteDetalle?.periodo}
                </p>
              </DialogHeader>

              {clienteDetalle && (
                <div className="space-y-6">
                  {/* Financial Metrics */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-2xl p-5">
                      <div className="stat-label">Ingresos</div>
                      <div className="text-lg font-light text-emerald-700 mt-1">
                        {formatCurrency(parseFloat(clienteDetalle.ingresos_operacionales) || 0)}
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-2xl p-5">
                      <div className="stat-label">Gastos</div>
                      <div className="text-lg font-light text-red-600 mt-1">
                        {formatCurrency(parseFloat(clienteDetalle.gastos_operacionales) || 0)}
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-2xl p-5">
                      <div className="stat-label">Resultado</div>
                      <div
                        className={cn(
                          'text-lg font-light mt-1',
                          parseFloat(clienteDetalle.resultado_neto) >= 0 ? 'text-emerald-700' : 'text-red-600'
                        )}
                      >
                        {formatCurrency(parseFloat(clienteDetalle.resultado_neto) || 0)}
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-2xl p-5">
                      <div className="stat-label">Margen</div>
                      <div className="text-lg font-light text-calm-text mt-1">
                        {parseFloat(clienteDetalle.margen_neto)?.toFixed(1) || 0}%
                      </div>
                    </div>
                  </div>

                  {/* Financial Ratios */}
                  {!!clienteDetalle.ratio_liquidez && (
                    <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-2xl p-6">
                      <h3 className="text-sm font-medium text-calm-text mb-5">Ratios Financieros</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        <div>
                          <div className="stat-label">Liquidez</div>
                          <div className="stat-value mt-1">
                            {clienteDetalle.ratio_liquidez ? parseFloat(clienteDetalle.ratio_liquidez).toFixed(2) : '-'}
                          </div>
                        </div>
                        <div>
                          <div className="stat-label">Solvencia</div>
                          <div className="stat-value mt-1">
                            {clienteDetalle.ratio_solvencia ? parseFloat(clienteDetalle.ratio_solvencia).toFixed(2) : '-'}
                          </div>
                        </div>
                        <div>
                          <div className="stat-label">ROI</div>
                          <div className="stat-value mt-1">
                            {clienteDetalle.roi ? parseFloat(clienteDetalle.roi).toFixed(1) : '-'}%
                          </div>
                        </div>
                        <div>
                          <div className="stat-label">ROE</div>
                          <div className="stat-value mt-1">
                            {clienteDetalle.roe ? parseFloat(clienteDetalle.roe).toFixed(1) : '-'}%
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* AI Commentary */}
                  {!!clienteDetalle.comentario_ia && (
                    <div className="bg-slate-50 border border-calm-border rounded-2xl p-6">
                      <div className="flex items-start gap-3">
                        <Icon name="smart_toy" className="text-[22px] text-calm-accent" />
                        <div>
                          <h3 className="text-sm font-medium text-calm-text mb-2">Análisis IA</h3>
                          <p className="text-sm text-calm-muted font-light">{clienteDetalle.comentario_ia}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Alerts */}
                  {clienteDetalle.alertas_financieras && clienteDetalle.alertas_financieras.length > 0 && (
                    <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-2xl p-6">
                      <h3 className="text-sm font-medium text-calm-text mb-4">Alertas</h3>
                      <div className="space-y-2">
                        {clienteDetalle.alertas_financieras.map((alerta, idx) => (
                          <div
                            key={idx}
                            className={cn(
                              'p-3 rounded-xl',
                              alerta.tipo === 'danger' ? 'bg-red-50' :
                              alerta.tipo === 'warning' ? 'bg-amber-50' :
                              'bg-slate-50'
                            )}
                          >
                            <div className="flex items-start gap-2">
                              <Badge
                                variant="outline"
                                className={cn('text-[9px] uppercase tracking-wider', getAlertBadgeClasses(alerta.tipo))}
                              >
                                {alerta.tipo.toUpperCase()}
                              </Badge>
                              <div className="flex-1">
                                <div className="text-sm font-light text-calm-text">{alerta.titulo}</div>
                                <div className="text-[11px] text-calm-muted font-light">{alerta.descripcion}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recommendations */}
                  {clienteDetalle.recomendaciones_ia && clienteDetalle.recomendaciones_ia.length > 0 && (
                    <div className="bg-emerald-50/50 border border-emerald-200 rounded-2xl p-6">
                      <div className="flex items-center gap-2 mb-4">
                        <Icon name="lightbulb" className="text-[20px] text-emerald-600" />
                        <h3 className="text-sm font-medium text-emerald-800">Recomendaciones IA</h3>
                      </div>
                      <ul className="space-y-2">
                        {clienteDetalle.recomendaciones_ia.map((rec, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-emerald-500 text-xs mt-0.5">•</span>
                            <span className="text-sm text-emerald-800 font-light">{rec}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <DialogFooter>
                    <Button
                      onClick={() => router.push(`/asesor/documentos-pendientes?cliente=${clienteDetalle.cliente_id}`)}
                    >
                      <Icon name="description" className="text-[16px]" />
                      Ver Documentos
                    </Button>
                    <button
                      onClick={() => router.push(`/asesor/incidencias?cliente=${clienteDetalle.cliente_id}`)}
                      className="ghost-btn"
                    >
                      <Icon name="report_problem" className="text-[16px]" />
                      Ver Incidencias
                    </button>
                  </DialogFooter>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </Layout>
    </>
  );
}
